import { ItineraryEvent, ItineraryEventType } from 'core/graphql/types';
import { format, parseISO } from 'date-fns';
import { BoxIcon, LocationIcon, TruckIcon } from 'components/svgs';
import EventStatusIndicator from 'components/eventStatusIndicator/EventStatusIndicator';
import classNames from 'classnames';
import Tooltip from 'components/tooltip/Tooltip';
import EventItemStatus, {
  EventStatus,
} from '../eventItemStatus/EventItemStatus';

export interface ItineraryEventItemProps {
  event: ItineraryEvent;
  onEventClick: (event: ItineraryEvent) => void;
}
const ItineraryEventItem = ({
  event,
  onEventClick,
}: ItineraryEventItemProps) => {
  const getEventStatus = (
    etaStartTime: string,
    etaEndTime: string,
    actualTime: string,
  ): EventStatus => {
    const parsedEtaStartTime = parseISO(etaStartTime);
    const parsedEtaEndTime = parseISO(etaEndTime);
    const parsedActualTime = parseISO(actualTime);

    if (
      parsedActualTime > parsedEtaEndTime ||
      parsedActualTime < parsedEtaStartTime
    ) {
      return EventStatus.Late;
    }

    return EventStatus.OnTime;
  };

  const renderItemInterval = (eventItem: ItineraryEvent) => {
    switch (eventItem.eventType) {
      case ItineraryEventType.Pickup:
      case ItineraryEventType.PudoVisit:
      case ItineraryEventType.LockerVisit:
        return (
          <EventItemStatus
            status={
              eventItem.visitTimeWindowStart && eventItem.visitTimeWindowEnd
                ? getEventStatus(
                    eventItem.visitTimeWindowStart,
                    eventItem.visitTimeWindowEnd,
                    eventItem.actualTime,
                  )
                : EventStatus.OnTime
            }
            arrival={format(parseISO(eventItem.actualTime), 'HH:mm')}
          />
        );

      case ItineraryEventType.Cancellation:
        return (
          <EventItemStatus
            status={EventStatus.Cancelled}
            arrival={format(parseISO(eventItem.actualTime), 'HH:mm')}
          />
        );
      default:
        return (
          <EventItemStatus
            status={getEventStatus(
              eventItem.etaStartTime,
              eventItem.etaEndTime,
              eventItem.actualTime,
            )}
            arrival={format(parseISO(eventItem.actualTime), 'HH:mm')}
          />
        );
    }
  };

  const getStartingTime = (eventItem: ItineraryEvent) => {
    if (
      eventItem.eventType === ItineraryEventType.Pickup ||
      eventItem.eventType === ItineraryEventType.PudoVisit ||
      eventItem.eventType === ItineraryEventType.LockerVisit
    ) {
      if (eventItem.visitTimeWindowStart) {
        return format(parseISO(eventItem.visitTimeWindowStart), 'HH:mm');
      }

      if (eventItem.eventType === ItineraryEventType.LockerVisit) {
        return '00:00';
      }
    }

    if (eventItem.etaStartTime) {
      return format(parseISO(eventItem.etaStartTime), 'HH:mm');
    }

    if (eventItem.visitTimeWindowStart) {
      return format(parseISO(eventItem.visitTimeWindowStart), 'HH:mm');
    }

    return (
      <Tooltip text="ETA could not be determined for non-geocoded stops">
        N/A
      </Tooltip>
    );
  };

  const getEndTime = (eventItem: ItineraryEvent) => {
    if (
      eventItem.eventType === ItineraryEventType.Pickup ||
      eventItem.eventType === ItineraryEventType.PudoVisit ||
      eventItem.eventType === ItineraryEventType.LockerVisit
    ) {
      if (eventItem.visitTimeWindowEnd) {
        return format(parseISO(eventItem.visitTimeWindowEnd), 'HH:mm');
      }

      if (eventItem.eventType === ItineraryEventType.LockerVisit) {
        return '23:59';
      }
    }

    if (eventItem.etaEndTime) {
      return format(parseISO(eventItem.etaEndTime), 'HH:mm');
    }

    if (eventItem.visitTimeWindowEnd) {
      return format(parseISO(eventItem.visitTimeWindowEnd), 'HH:mm');
    }

    return (
      <Tooltip text="ETA could not be determined for non-geocoded stops">
        N/A
      </Tooltip>
    );
  };

  return (
    <div
      onClick={() => onEventClick(event)}
      role="button"
      aria-hidden="true"
      tabIndex={-1}
      className={classNames(
        'relative before:absolute before:top-0 before:-ml-2 before:h-full before:w-5 before:bg-dotted-vertical-line before:bg-center before:bg-repeat-y',
        {
          'before:content-green-big-dot':
            event.eventType === ItineraryEventType.Delivery,
        },
        {
          'before:content-yellow-big-dot':
            event.eventType === ItineraryEventType.Cancellation,
        },
        {
          'before:content-magenta-big-dot':
            event.eventType === ItineraryEventType.Pickup,
        },
        {
          'before:content-pink-big-dot':
            event.eventType === ItineraryEventType.StandardRetour ||
            event.eventType === ItineraryEventType.PickPackRetour,
        },
        {
          'before:content-blue-big-dot':
            event.eventType === ItineraryEventType.LockerVisit,
        },
        {
          'before:content-orange-big-dot':
            event.eventType === ItineraryEventType.PudoVisit,
        },
        {
          'before:content-dark-blue-big-dot':
            event.eventType === ItineraryEventType.C2CPickup,
        },
      )}
    >
      <div className="py-2 pl-6">
        <div className="flex items-start gap-4">
          <div className="mb-3">
            {event.eventType === ItineraryEventType.Pickup ? (
              <p>Pickup Point Alias</p>
            ) : event.eventType === ItineraryEventType.LockerVisit ? (
              <p>Locker Name</p>
            ) : event.eventType === ItineraryEventType.PudoVisit ? (
              <p>PUDO Name</p>
            ) : (
              <p>AWB</p>
            )}
            <p>{event.identifierDescription}</p>
          </div>
          <div className="mb-3">
            {event.eventType === ItineraryEventType.Pickup ? (
              <p>Pickup ID</p>
            ) : event.eventType === ItineraryEventType.LockerVisit ? (
              <p>Locker ID</p>
            ) : event.eventType === ItineraryEventType.PudoVisit ? (
              <p>PUDO ID</p>
            ) : (
              <p>AWB ID</p>
            )}
            <p>{event.identifier}</p>
          </div>
          {event.eventType === ItineraryEventType.Pickup && (
            <div className="mb-3 max-w-[12rem] 2xl:max-w-sm">
              <p>Pickup AWBs</p>
              <p className="break-words">
                {event.pickedUpAwbNumbers?.replaceAll(',', ' ')}
              </p>
            </div>
          )}
          <div className="mb-3 ml-auto">
            <EventStatusIndicator eventType={event.eventType} />
          </div>
        </div>
        <div className="mb-6 bg-muted-white dark:bg-design-secondary-black">
          <div className="p-4">
            <div className="flex w-full">
              <div className="mr-4 w-10/12 px-4">
                <div className="mb-10 flex 2xl:mb-3">
                  <div className="w-1/5">
                    <TruckIcon className="relative ml-1 mt-2 fill-design-gray dark:fill-white" />
                  </div>
                  <div className="w-4/5">
                    <p>ETA</p>
                    <div className="flex items-center">
                      <span className="relative z-20">{getStartingTime(event)}</span>
                      {renderItemInterval(event)}
                      <span className="relative z-20">{getEndTime(event)}</span>
                    </div>
                  </div>
                </div>
                <div className="mb-3 flex">
                  <div className="w-1/5">
                    <BoxIcon className="relative ml-1 mt-2 fill-design-gray dark:fill-white" />
                  </div>
                  <div className="w-4/5">
                    <p>Address</p>
                    <div className="flex items-center">{event.address}</div>
                  </div>
                </div>
                <div className="mb-3 flex">
                  <div className="w-1/5">
                    <LocationIcon className="relative ml-1 mt-2 fill-design-gray dark:fill-white" />
                  </div>
                  <div className="flex w-4/5 justify-between">
                    <div>
                      <p>Town</p>
                      <div className="flex items-center">{event.town}</div>
                    </div>
                    <div>
                      <p>Village</p>
                      <div className="flex items-center">{event.village}</div>
                    </div>
                    <div>
                      <p>County</p>
                      <div className="flex items-center">{event.county}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex w-2/12 flex-col justify-evenly border-l-2 border-design-black px-4">
                <p className="ml-auto mr-0 w-fit">Order</p>
                <div className="ml-auto mr-0 w-9 bg-design-gray px-2 py-1 text-right text-white">
                  {event.itineraryOrder}
                </div>
                <p className="ml-auto mr-0 w-fit">Actual Order</p>
                <div
                  className={`ml-auto mr-0 w-9 bg-design-gray px-2 py-1 text-right text-white ${
                    event.actualOrder &&
                    event.itineraryOrder !== event.actualOrder &&
                    'border-l-2 border-design-red'
                  }`}
                >
                  {event.actualOrder && event.actualOrder > 0
                    ? event.actualOrder
                    : 'NA'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryEventItem;
